import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { formatCategory } from '../utils/categories';
import ChartFrame from './ChartFrame';

interface SeriesPoint {
  date: string;
  amount: number;
  merchant: string;
  category: string;
}

// Chart data point: each transaction becomes one point; same-day tx get an index suffix
interface ChartPoint extends SeriesPoint {
  xLabel: string; // display label for X axis
}

interface Props {
  label: string;
  filterType: 'merchant' | 'category';
  series: SeriesPoint[];
}

const formatCurrency = (value: number) =>
  value >= 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${value.toFixed(0)}`;

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload as ChartPoint;
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 6,
      padding: '8px 12px', fontSize: 12, lineHeight: 1.6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 600 }}>{d.merchant}</div>
      <div style={{ color: '#667eea', fontWeight: 700 }}>${d.amount.toFixed(2)}</div>
      <div style={{ color: '#718096' }}>{formatCategory(d.category)}</div>
      <div style={{ color: '#a0aec0' }}>{formatDate(d.date)}</div>
    </div>
  );
};

const SpendingTrendChart: React.FC<Props> = ({ label, filterType, series }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (!series || !series.length) {
    return (
      <div className="chart-wrapper">
        <p className="chart-empty">No transactions found for {label}.</p>
      </div>
    );
  }

  // Build chart data: assign unique xLabels, handle same-day duplicates
  const dateCounts: Record<string, number> = {};
  const chartData: ChartPoint[] = series.map(d => {
    const base = formatDate(d.date);
    dateCounts[base] = (dateCounts[base] || 0) + 1;
    const count = dateCounts[base];
    return { ...d, xLabel: count > 1 ? `${base} (${count})` : base };
  });

  const avg = series.reduce((s, d) => s + d.amount, 0) / series.length;

  const title = filterType === 'merchant'
    ? `${label} — per transaction`
    : `${label} — per transaction`;

  const renderChart = (fullscreen: boolean) => (
    <ResponsiveContainer width="100%" height={fullscreen ? '100%' : 280}>
      <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 32, left: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
        <XAxis
          dataKey="xLabel"
          tick={{ fontSize: 10 }}
          angle={-35}
          textAnchor="end"
          interval={0}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 11 }}
          width={58}
        />
        <Tooltip content={<CustomTooltip />} />
        {avg > 0 && (
          <ReferenceLine
            y={avg}
            stroke="#f6ad55"
            strokeDasharray="4 3"
            label={{ value: `avg $${avg.toFixed(0)}`, position: 'insideTopRight', fontSize: 10, fill: '#b7791f' }}
          />
        )}
        <Line
          type="monotone"
          dataKey="amount"
          stroke="#667eea"
          strokeWidth={2}
          dot={{ r: 5, fill: '#667eea', stroke: '#764ba2', strokeWidth: 1.5 }}
          activeDot={{ r: 7, fill: '#764ba2' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderTable = () => (
    <table className="chart-data-table">
      <thead>
        <tr><th>Date</th><th>Merchant</th><th>Category</th><th>Amount</th></tr>
      </thead>
      <tbody>
        {series.map((d, i) => (
          <tr key={i}>
            <td>{formatDate(d.date)}</td>
            <td>{d.merchant}</td>
            <td>{formatCategory(d.category)}</td>
            <td>${d.amount.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <ChartFrame
      title={title}
      showRaw={showRaw}
      onToggle={setShowRaw}
      renderChart={renderChart}
      renderTable={renderTable}
    />
  );
};

export default SpendingTrendChart;
